import { GeneralError } from '51trips-frontend-core'
import { ConfirmationResult, RecaptchaVerifier } from 'firebase/auth'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmPhoneVerificationCodeUseCase } from '../../domain/useCases/auth/ConfirmPhoneVerificationCodeUseCase'
import { SendPhoneVerificationCodeUseCase } from '../../domain/useCases/auth/SendPhoneVerificationCodeUseCase'
import { auth } from '../../firebase'
import { Button } from '../Button'
import { FormGroup } from '../FormGroup'
import { Input } from '../Input'
import { AuthBackButton } from './AuthBackButton'
import { AuthErrorBanner } from './AuthErrorBanner'
import { CountryCodeSelect } from './CountryCodeSelect'
import { OtpCodeInput } from './OtpCodeInput'

const RESEND_COOLDOWN_SECONDS = 60

interface PhoneLoginFormProps {
  sendPhoneVerificationCodeUseCase: SendPhoneVerificationCodeUseCase
  confirmPhoneVerificationCodeUseCase: ConfirmPhoneVerificationCodeUseCase
  onSuccess: () => void
  onBack: () => void
}

export const PhoneLoginForm: React.FC<PhoneLoginFormProps> = ({
  sendPhoneVerificationCodeUseCase,
  confirmPhoneVerificationCodeUseCase,
  onSuccess,
  onBack
}) => {
  const { t } = useTranslation()

  const [step, setStep] = useState<'number' | 'code'>('number')
  const [dialCode, setDialCode] = useState('+34')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [code, setCode] = useState('')
  const [codeInputResetKey, setCodeInputResetKey] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)

  const recaptchaContainerRef = useRef<HTMLDivElement>(null)
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null)
  const confirmationResultRef = useRef<ConfirmationResult | null>(null)

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return
    }
    const timeout = setTimeout(() => setResendSecondsLeft((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timeout)
  }, [resendSecondsLeft])

  const resetRecaptcha = () => {
    recaptchaVerifierRef.current?.clear()
    recaptchaVerifierRef.current = null
  }

  const getOrCreateRecaptchaVerifier = () => {
    if (!recaptchaVerifierRef.current && recaptchaContainerRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
        size: 'invisible'
      })
    }
    return recaptchaVerifierRef.current
  }

  const fullPhoneNumber = `${dialCode}${phoneNumber.replace(/\s+/g, '')}`

  const sendVerificationCode = () => {
    const recaptchaVerifier = getOrCreateRecaptchaVerifier()
    if (!phoneNumber || !recaptchaVerifier || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(undefined)

    sendPhoneVerificationCodeUseCase
      .sendPhoneVerificationCode(fullPhoneNumber, recaptchaVerifier)
      .subscribe({
        next: (confirmationResult) => {
          confirmationResultRef.current = confirmationResult
          setIsSubmitting(false)
          setStep('code')
          setResendSecondsLeft(RESEND_COOLDOWN_SECONDS)
        },
        error: (error: GeneralError) => {
          resetRecaptcha()
          setErrorMessage(error.message || t('phoneLogin.sendCodeError', { ns: 'auth' }))
          setIsSubmitting(false)
        }
      })
  }

  const handleSubmitPhoneNumber = (event: FormEvent) => {
    event.preventDefault()
    sendVerificationCode()
  }

  const handleSubmitCode = (event: FormEvent) => {
    event.preventDefault()

    const confirmationResult = confirmationResultRef.current
    if (!confirmationResult || code.length < 6 || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(undefined)

    confirmPhoneVerificationCodeUseCase
      .confirmPhoneVerificationCode(confirmationResult, code)
      .subscribe({
        next: () => {
          setIsSubmitting(false)
          onSuccess()
        },
        error: (error: GeneralError) => {
          setErrorMessage(error.message || t('phoneLogin.invalidCode', { ns: 'auth' }))
          setIsSubmitting(false)
        }
      })
  }

  const handleResendCode = () => {
    if (resendSecondsLeft > 0) {
      return
    }
    setCode('')
    setCodeInputResetKey((key) => key + 1)
    resetRecaptcha()
    sendVerificationCode()
  }

  const formatCountdown = (seconds: number) => `0:${seconds.toString().padStart(2, '0')}`

  return (
    <div className="flex flex-col gap-5">
      <div ref={recaptchaContainerRef} />

      {step === 'number' ? (
        <>
          <AuthBackButton onClick={onBack} label={t('loginMethods.otherMethods', { ns: 'auth' })} />

          <div className="flex flex-col gap-1.5">
            <h1 className="text-[21px] font-semibold tracking-tight text-neutralBlack-900">
              {t('phoneLogin.title', { ns: 'auth' })}
            </h1>
            <p className="text-[13.5px] text-neutralBlack-500">
              {t('phoneLogin.description', { ns: 'auth' })}
            </p>
          </div>

          {errorMessage ? <AuthErrorBanner message={errorMessage} /> : null}

          <form onSubmit={handleSubmitPhoneNumber} className="flex flex-col gap-4">
            <FormGroup label={t('phoneLogin.phoneInput.label', { ns: 'auth' })}>
              <div className="flex gap-2">
                <CountryCodeSelect value={dialCode} onChange={setDialCode} disabled={isSubmitting} />
                <Input
                  value={phoneNumber}
                  onChange={(event: { target: { value: string } }) =>
                    setPhoneNumber(event.target.value)
                  }
                  block
                  placeholder={t('phoneLogin.phoneInput.placeholder', { ns: 'auth' })}
                  type="tel"
                  autoComplete="tel"
                  required
                />
              </div>
            </FormGroup>

            <Button
              type="submit"
              variant="primary"
              size="large"
              label={t('phoneLogin.sendCode', { ns: 'auth' })}
              disabled={isSubmitting}
              block
            />
          </form>
        </>
      ) : (
        <>
          <AuthBackButton
            onClick={() => setStep('number')}
            label={t('phoneLogin.changeNumber', { ns: 'auth' })}
          />

          <div className="flex flex-col gap-1.5">
            <h1 className="text-[21px] font-semibold tracking-tight text-neutralBlack-900">
              {t('phoneLogin.codeTitle', { ns: 'auth' })}
            </h1>
            <p className="text-[13.5px] text-neutralBlack-500">
              {t('phoneLogin.codeDescription', { ns: 'auth', phoneNumber: fullPhoneNumber })}
            </p>
          </div>

          {errorMessage ? <AuthErrorBanner message={errorMessage} /> : null}

          <form onSubmit={handleSubmitCode} className="flex flex-col gap-4">
            <OtpCodeInput
              key={codeInputResetKey}
              onChange={setCode}
              disabled={isSubmitting}
            />

            <Button
              type="submit"
              variant="primary"
              size="large"
              label={t('phoneLogin.verifyCode', { ns: 'auth' })}
              disabled={isSubmitting || code.length < 6}
              block
            />

            {resendSecondsLeft > 0 ? (
              <p className="text-center text-xs text-neutralBlack-400">
                {t('phoneLogin.resendIn', { ns: 'auth', time: formatCountdown(resendSecondsLeft) })}
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendCode}
                className="text-center text-xs text-neutralBlack-500 hover:text-neutralBlack-900 cursor-pointer"
              >
                {t('phoneLogin.resendCode', { ns: 'auth' })}
              </button>
            )}
          </form>
        </>
      )}
    </div>
  )
}
